"use client"

import { useState } from "react"
import { useAuth } from "../../context/AuthContext"
import { useLanguage } from "../../context/LanguageContext"
import api from "../../utils/api"
import toast from "react-hot-toast"
import { Heart, CreditCard, Smartphone, X } from "lucide-react"

const EnhancedDonationButton = ({ onSuccess }) => {
  const { user } = useAuth()
  const { t } = useLanguage()
  const [loading, setLoading] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)

  const handleRazorpayPayment = async () => {
    setLoading(true)

    try {
      // Create order
      const orderResponse = await api.post("/donations/create-order")
      const { orderId, amount, currency, key } = orderResponse.data

      // Razorpay options
      const options = {
        key: key,
        amount: amount,
        currency: currency,
        name: "Donation App",
        description: "Membership Donation",
        order_id: orderId,
        prefill: {
          name: user.name,
          email: user.email,
          contact: user.phone,
        },
        theme: {
          color: "#2563eb",
        },
        handler: async (response) => {
          try {
            // Verify payment
            await api.post("/donations/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            })

            toast.success(t("payment.success"))
            setShowPaymentModal(false)
            if (onSuccess) onSuccess()
          } catch (error) {
            toast.error(t("payment.failed"))
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false)
            setShowPaymentModal(false)
          },
        },
      }

      // Open Razorpay
      const rzp = new window.Razorpay(options)
      rzp.open()
    } catch (error) {
      toast.error("Failed to initiate payment")
      setLoading(false)
    }
  }

  const handleUPIPayment = async () => {
    setLoading(true)

    try {
      // Create order
      const orderResponse = await api.post("/donations/create-order")
      const { orderId, amount, currency, key } = orderResponse.data

      // UPI specific options
      const options = {
        key: key,
        amount: amount,
        currency: currency,
        name: "Donation App",
        description: "Membership Donation",
        order_id: orderId,
        prefill: {
          name: user.name,
          email: user.email,
          contact: user.phone,
        },
        method: {
          upi: true,
        },
        theme: {
          color: "#2563eb",
        },
        handler: async (response) => {
          try {
            // Verify payment
            await api.post("/donations/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            })

            toast.success(t("payment.success"))
            setShowPaymentModal(false)
            if (onSuccess) onSuccess()
          } catch (error) {
            toast.error(t("payment.failed"))
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false)
            setShowPaymentModal(false)
          },
        },
      }

      // Open Razorpay with UPI focus
      const rzp = new window.Razorpay(options)
      rzp.open()
    } catch (error) {
      toast.error("Failed to initiate UPI payment")
      setLoading(false)
    }
  }

  const detectAndOpenUPIApp = () => {
    const upiId = "donation@upi" // Replace with your actual UPI ID
    const amount = "100"
    const note = "Donation App Membership"

    // UPI deep link
    const upiLink = `upi://pay?pa=${upiId}&pn=Donation App&am=${amount}&cu=INR&tn=${encodeURIComponent(note)}`

    // Try to open UPI app
    const userAgent = navigator.userAgent.toLowerCase()

    if (userAgent.includes("android")) {
      // Android - try to open UPI app
      window.location.href = upiLink

      // Fallback after 2 seconds
      setTimeout(() => {
        toast.error("Please install a UPI app or use Razorpay payment")
      }, 2000)
    } else if (userAgent.includes("iphone") || userAgent.includes("ipad")) {
      // iOS - UPI apps might not support deep links well
      toast.info("Please use Razorpay payment for iOS devices")
    } else {
      // Desktop - show QR code or redirect to web UPI
      toast.info("UPI payment is best supported on mobile devices")
    }
  }

  return (
    <>
      <div className="text-center">
        <button
          onClick={() => setShowPaymentModal(true)}
          disabled={loading}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <Heart className="mr-2 h-5 w-5" />
          {loading ? t("payment.processing") : t("dashboard.joinMembership")}
        </button>
        <p className="mt-2 text-sm text-gray-600">{t("payment.fixedAmount")}</p>
      </div>

      {/* Payment Method Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t("payment.selectMethod")}</h3>
              <button onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Razorpay Payment */}
              <button
                onClick={handleRazorpayPayment}
                disabled={loading}
                className="w-full flex items-center justify-center space-x-3 px-4 py-3 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <CreditCard className="h-5 w-5 text-blue-600" />
                <span className="font-medium">{t("payment.razorpay")}</span>
              </button>

              {/* UPI Payment */}
              <button
                onClick={handleUPIPayment}
                disabled={loading}
                className="w-full flex items-center justify-center space-x-3 px-4 py-3 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <Smartphone className="h-5 w-5 text-green-600" />
                <span className="font-medium">{t("payment.upi")}</span>
              </button>

              {/* Direct UPI App */}
              <button
                onClick={detectAndOpenUPIApp}
                disabled={loading}
                className="w-full flex items-center justify-center space-x-3 px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              >
                <Smartphone className="h-5 w-5" />
                <span className="font-medium">Open UPI App</span>
              </button>
            </div>

            <div className="mt-4 text-center">
              <p className="text-sm text-gray-500">{t("payment.fixedAmount")}</p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default EnhancedDonationButton
